import React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import reportApiFailure from 'shared/api/reportApiFailure'
import { saveBlobAsFile } from 'shared/file/saveBlobAsFile'
import { fetchNewsAttachmentUrl } from '../api/fetchNewsAttachmentUrl'
import { NewsAttachment } from '../models/types'

interface NewsAttachmentListProps {
  postId: number
  attachments: NewsAttachment[]
}

function NewsAttachmentList({ postId, attachments }: Readonly<NewsAttachmentListProps>) {
  const { t } = useTranslation()

  // Never a bare href: a plain link would skip the axios interceptor and 401. The signed link is
  // fetched on demand, and its bytes are fetched without our auth header so the token never
  // reaches storage.
  const saveAttachment = async (attachment: NewsAttachment) => {
    try {
      const signedUrl = await fetchNewsAttachmentUrl(postId, attachment.id)
      const response = await fetch(signedUrl, { cache: 'no-cache' })
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`)
      }

      saveBlobAsFile(await response.blob(), attachment.filename)
    } catch (error) {
      reportApiFailure(error, () => toast.error(t('news.downloadFailed')))
    }
  }

  if (attachments.length === 0) {
    return null
  }

  return (
    <ul className="news-feed__item-attachments" data-testid="news-attachments">
      {attachments.map((attachment) => (
        <li key={attachment.id}>
          <button
            type="button"
            className="btn btn-link btn-sm px-0"
            title={t('news.downloadAttachment')}
            onClick={() => saveAttachment(attachment)}
          >
            <i className="fal fa-paperclip me-2" />
            {attachment.filename}
          </button>
        </li>
      ))}
    </ul>
  )
}

export default NewsAttachmentList
